import { createContext, useContext, useEffect, useState } from 'react';
import { useBranding } from '@/hooks/useBranding';

interface BrandingContextType {
  organizationName: string;
  logoUrl: string | null;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  headerMode: 'light' | 'dark';
}

const BrandingContext = createContext<BrandingContextType>({
  organizationName: 'Shelter Management System',
  logoUrl: null,
  primaryColor: '#2563eb',
  secondaryColor: '#eff6ff',
  accentColor: '#dbeafe',
  headerMode: 'light'
});

export const useBrandingContext = () => useContext(BrandingContext);

export function BrandingProvider({ children }: { children: React.ReactNode }) {
  const { branding } = useBranding();

  useEffect(() => {
    if (branding) {
      // Apply CSS variables for theming
      const root = document.documentElement;
      
      // Convert hex to HSL for CSS variables
      const hexToHSL = (hex: string) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        if (!result) return '0 0% 0%';
        
        const r = parseInt(result[1], 16) / 255;
        const g = parseInt(result[2], 16) / 255;
        const b = parseInt(result[3], 16) / 255;

        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        let h = 0, s = 0;
        const l = (max + min) / 2;

        if (max !== min) {
          const d = max - min;
          s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
          
          switch (max) {
            case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
            case g: h = ((b - r) / d + 2) / 6; break;
            case b: h = ((r - g) / d + 4) / 6; break;
          }
        }

        return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
      };

      root.style.setProperty('--primary', hexToHSL(branding.primary_color));
      root.style.setProperty('--secondary', hexToHSL(branding.secondary_color));
      root.style.setProperty('--accent', hexToHSL(branding.accent_color));
    }
  }, [branding]);

  return (
    <BrandingContext.Provider value={{
      organizationName: branding?.organization_name || 'Shelter Management System',
      logoUrl: branding?.logo_url || null,
      primaryColor: branding?.primary_color || '#2563eb',
      secondaryColor: branding?.secondary_color || '#eff6ff',
      accentColor: branding?.accent_color || '#dbeafe',
      headerMode: branding?.header_mode || 'light'
    }}>
      {children}
    </BrandingContext.Provider>
  );
}
